// src/redux/slices/projectSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import createAxiosInstance from "../Axios"; // Adjust the path accordingly
// import PQueue from "p-queue";
import axios from "axios";

// Helper functions for session storage
const getSessionStorageKey = (apiType, projectId, runId) => {
  return `${apiType}_${projectId}_${runId}`;
};

const getFromSessionStorage = (key) => {
  try {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error reading from session storage:", error);
    return null;
  }
};

const saveToSessionStorage = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving to session storage:", error);
  }
};

const removeFromSessionStorage = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error("Error removing from session storage:", error);
  }
};

// Thunk: Create project and extract Amazon PDP data
export const createProject = createAsyncThunk(
  "project/createProject",
  async (url, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const projectResponse = await axiosInstance.post(`/v1/projects/`, {});
      const projectData = projectResponse.data;

      let amazonData = null;

      if (url.includes("https")) {
        const extractResponse = await axiosInstance.get(
          `/v1/util/extract_from_amazon_pdp_url`,
          {
            params: { url },
          }
        );
        amazonData = extractResponse.data || null;
      }

      return {
        project: projectData,
        amazonInfo: amazonData,
        url,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// Thunk: Update product details
export const updateProduct = createAsyncThunk(
  "project/updateProduct",
  async ({ projectId, productData }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.put(
        `/v1/projects/${projectId}/project_details/main`,
        productData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update product"
      );
    }
  }
);

// Thunk: Get product about details
export const getProduct = createAsyncThunk(
  "project/getProductAbout",
  async (_, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(`/v1/projects/`, {
        params: {
          include_details: true
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch product about details"
      );
    }
  }
);

// Thunk: Get single project details by ID (for read-only view)
export const getProjectDetails = createAsyncThunk(
  "project/getProjectDetails",
  async (projectId, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(`/v1/projects/${projectId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch project details"
      );
    }
  }
);

// Thunk: Get project details product info
export const getProjectProductInfo = createAsyncThunk(
  "project/getProjectProductInfo",
  async (projectId, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      // MODIFICATION: Remove the trailing slash from the URL
      const response = await axiosInstance.get(
        `/v1/projects/${projectId}/product_info`
      );
      //console.log('API response for /product_info:', response.data);
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching project product info (no slash):",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to fetch project product info"
      );
    }
  }
);

// Thunk: Get Title
export const getProjectTitle = createAsyncThunk(
  "project/getProjectTitle",
  async ({ projectId, runId, forceRefresh = false }, { rejectWithValue }) => {
    try {
      const cacheKey = getSessionStorageKey("title", projectId, runId);

      // Check session storage first unless force refresh is requested
      if (!forceRefresh) {
        const cachedData = getFromSessionStorage(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `/v1/projects/${projectId}/${runId}/title`
      );
      //console.log('API response for /title:', response.data);

      // Save to session storage
      saveToSessionStorage(cacheKey, response.data);

      return response.data;
    } catch (error) {
      console.error(
        "Error fetching project title:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to fetch project title"
      );
    }
  }
);

// Thunk: Get Features
export const getProjectFeatures = createAsyncThunk(
  "project/getProjectFeatures",
  async ({ projectId, runId, forceRefresh = false }, { rejectWithValue }) => {
    try {
      const cacheKey = getSessionStorageKey("features", projectId, runId);

      // Check session storage first unless force refresh is requested
      if (!forceRefresh) {
        const cachedData = getFromSessionStorage(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `/v1/projects/${projectId}/${runId}/features`
      );
      //console.log('API response for /features:', response.data);

      // Save to session storage
      saveToSessionStorage(cacheKey, response.data);

      return response.data;
    } catch (error) {
      console.error(
        "Error fetching project features:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to fetch project features"
      );
    }
  }
);

// Thunk: Get Description
export const getProjectDescription = createAsyncThunk(
  "project/getProjectDescription",
  async ({ projectId, runId, forceRefresh = false }, { rejectWithValue }) => {
    try {
      const cacheKey = getSessionStorageKey("description", projectId, runId);

      // Check session storage first unless force refresh is requested
      if (!forceRefresh) {
        const cachedData = getFromSessionStorage(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `/v1/projects/${projectId}/${runId}/description`
      );
      //console.log('API response for /description:', response.data);

      // Save to session storage
      saveToSessionStorage(cacheKey, response.data);

      return response.data;
    } catch (error) {
      console.error(
        "Error fetching project description:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to fetch project description"
      );
    }
  }
);

// Thunk: Get Important Info
export const getProjectImportantInfo = createAsyncThunk(
  "project/getProjectImportantInfo",
  async ({ projectId, runId, forceRefresh = false }, { rejectWithValue }) => {
    try {
      const cacheKey = getSessionStorageKey("importantInfo", projectId, runId);

      // Check session storage first unless force refresh is requested
      if (!forceRefresh) {
        const cachedData = getFromSessionStorage(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `/v1/projects/${projectId}/${runId}/important_info`
      );
      // console.log('API response for /important_info:', response.data);

      // Save to session storage
      saveToSessionStorage(cacheKey, response.data);

      return response.data;
    } catch (error) {
      console.error(
        "Error fetching project important info:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to fetch project important info"
      );
    }
  }
);

// New Thunk: Load all cached data at once
export const loadAllCachedData = createAsyncThunk(
  "project/loadAllCachedData",
  async (
    { projectId, runId, forceRefresh = false },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const results = {};

      // Load all data concurrently
      const promises = [
        dispatch(getProjectTitle({ projectId, runId, forceRefresh })),
        dispatch(getProjectFeatures({ projectId, runId, forceRefresh })),
        dispatch(getProjectDescription({ projectId, runId, forceRefresh })),
        dispatch(getProjectImportantInfo({ projectId, runId, forceRefresh })),
      ];

      const responses = await Promise.allSettled(promises);

      // Process results
      responses.forEach((response, index) => {
        const apiNames = ["title", "features", "description", "importantInfo"];
        if (response.status === "fulfilled") {
          results[apiNames[index]] = response.value.payload;
        } else {
          console.error(`Failed to load ${apiNames[index]}:`, response.reason);
          results[apiNames[index]] = null;
        }
      });

      return results;
    } catch (error) {
      console.error("Error loading cached data:", error);
      return rejectWithValue("Failed to load cached data");
    }
  }
);

let currentAbortController = null;

export const getTitleAndImage = createAsyncThunk(
  "project/getTitleAndImage",
  async ({ asin, marketplace_full }, { rejectWithValue }) => {
    if (currentAbortController) {
      // console.log('Cancelling previous request...');
      currentAbortController.abort();
    }

    currentAbortController = new AbortController();
    const { signal } = currentAbortController;

    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(`/v1/projects/title_image`, {
        params: { asin, marketplace_full },
        signal: signal,
      });
      currentAbortController = null;

      return response.data;
    } catch (error) {
      if (axios.isCancel(error) || error.name === "AbortError") {
        console.warn("Request was cancelled:", error.message);
        return rejectWithValue({
          cancelled: true,
          message: "Request was cancelled by a new one.",
        });
      }

      if (error.response) {
        return rejectWithValue(error.response.data || "Server error");
      } else if (error.request) {
        return rejectWithValue("No response received from server");
      } else {
        return rejectWithValue(
          error.message || "Failed to fetch title and image"
        );
      }
    } finally {
      if (currentAbortController && currentAbortController.signal === signal) {
        currentAbortController = null;
      }
    }
  }
);

// Thunk: Get Suggested Competition URLs
export const getSuggestedCompetitionUrls = createAsyncThunk(
  "project/getSuggestedCompetitionUrls",
  async (projectId, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `/v1/projects/${projectId}/suggested_competition_urls`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch suggested competition URLs"
      );
    }
  }
);

// Thunk: Start project processing
export const startProjectProcessing = createAsyncThunk(
  "project/startProjectProcessing",
  async (projectId, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.post(
        `/v1/projects/${projectId}/start`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to start project processing"
      );
    }
  }
);

// Thunk: Get processing status
export const getProcessingStatus = createAsyncThunk(
  "project/getProcessingStatus",
  async ({ projectId, runId }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `/v1/projects/${projectId}/${runId}/processing_status`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch processing status"
      );
    }
  }
);

// Thunk: Set Product Competitors
export const setProductCompetitors = createAsyncThunk(
  "project/setProductCompetitors",
  async ({ projectId, competitors }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.put(
        `/v1/projects/${projectId}/competitors`,
        competitors
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to set product competitors"
      );
    }
  }
);

// Thunk: Upload Search Term Reports (Single File)
export const uploadSearchTermReports = createAsyncThunk(
  "project/uploadSearchTermReports",
  async ({ projectId, keyword, file, keywordIndex }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('upload_files', file);

      const response = await axiosInstance.put(
        `/v1/projects/${projectId}/project_details/files`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            // Can be used to track upload progress if needed
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload Progress for ${keyword}: ${percentCompleted}%`);
          }
        }
      );

      return {
        keywordIndex,
        keyword,
        fileName: file.name,
        data: response.data
      };
    } catch (error) {
      return rejectWithValue({
        keywordIndex,
        error: error.response?.data || "Failed to upload search term report"
      });
    }
  }
);

// Thunk: Upload All Search Term Reports (Batch)
export const uploadAllSearchTermReports = createAsyncThunk(
  "project/uploadAllSearchTermReports",
  async ({ projectId, files }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();

      // Create FormData for multiple file uploads
      const formData = new FormData();

      // Append all files to the upload_files array
      files.forEach((fileData) => {
        if (fileData.file) {
          formData.append('upload_files', fileData.file);
        }
      });

      const response = await axiosInstance.put(
        `/v1/projects/${projectId}/project_details/files`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Batch Upload Progress: ${percentCompleted}%`);
          }
        }
      );

      // Return data for all uploaded files
      return {
        files: files.map((fileData, index) => ({
          keywordIndex: fileData.keywordIndex,
          keyword: fileData.keyword,
          fileName: fileData.file.name,
        })),
        data: response.data
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to upload search term reports"
      );
    }
  }
);

// Thunk: Extract Amazon PDP URL data
export const extractAmazonUrlData = createAsyncThunk(
  "project/extractAmazonUrlData",
  async (url, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `/v1/util/extract_from_amazon_pdp_url`,
        {
          params: { url },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to extract Amazon URL data"
      );
    }
  }
);

// Thunk: Update/Title
export const updateProjectTitle = createAsyncThunk(
  "project/updateProjectTitle",
  async ({ projectId, runId, title }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.put(
        `/v1/projects/${projectId}/${runId}/title`,
        title
      );

      // Clear session storage cache for title since we updated it
      const cacheKey = getSessionStorageKey("title", projectId, runId);
      removeFromSessionStorage(cacheKey);

      return response.data;
    } catch (error) {
      console.error(
        "Error updating project title:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to update project title"
      );
    }
  }
);

// Thunk: Update/Features
export const updateProjectFeatures = createAsyncThunk(
  "project/updateProjectFeatures",
  async ({ projectId, runId, features }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.put(
        `/v1/projects/${projectId}/${runId}/features`,
        features
      );

      // Clear session storage cache for features since we updated it
      const cacheKey = getSessionStorageKey("features", projectId, runId);
      removeFromSessionStorage(cacheKey);

      return response.data;
    } catch (error) {
      console.error(
        "Error updating project feature:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to update project feature"
      );
    }
  }
);

// Thunk: Update/description
export const updateProjectDescription = createAsyncThunk(
  "project/updateProjectDescription",
  async ({ projectId, runId, description }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.put(
        `/v1/projects/${projectId}/${runId}/description`,
        description
      );

      // Clear session storage cache for description since we updated it
      const cacheKey = getSessionStorageKey("description", projectId, runId);
      removeFromSessionStorage(cacheKey);

      return response.data;
    } catch (error) {
      console.error(
        "Error updating project description:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to update project description"
      );
    }
  }
);

// Thunk: Update/Important info
export const updateProjectImportantInfo = createAsyncThunk(
  "project/updateProjectImportantInfo",
  async ({ projectId, runId, importantInfo }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.put(
        `/v1/projects/${projectId}/${runId}/important_info`,
        importantInfo
      );

      // Clear session storage cache for important info since we updated it
      const cacheKey = getSessionStorageKey("importantInfo", projectId, runId);
      removeFromSessionStorage(cacheKey);

      return response.data;
    } catch (error) {
      console.error(
        "Error updating project important info:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to update project important info"
      );
    }
  }
);

// Thunk: Get Audit Elements Count
export const getAuditElementsCount = createAsyncThunk(
  "project/getAuditElementsCount",
  async ({ projectId, runId }, { rejectWithValue }) => {
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        `/v1/projects/${projectId}/${runId}/audit_elements_count`
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching audit elements count:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to fetch audit elements count"
      );
    }
  }
);

const projectSlice = createSlice({
  name: "project",
  initialState: {
    project: null,
    amazonInfo: null,
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    url: "",
    projects_data: [],
    product_link: "",
    marketPlace: "us",
    productInfo: null,
    title: null,
    features: null,
    description: null,
    importantInfo: null,
    titleAndImage: null,
    suggestedCompetitionUrls: null,
    runId: null,
    processingStatus: null,
    isProcessing: false,
    selectedCompetitors: [],
    competitorsStatus: "idle",
    extractedUrlData: null,
    extractUrlStatus: "idle",
    allDataLoaded: false,
    loadingAllData: false,
    asinCode: "", // saves the ASIN of the product during asin input
    parentAsinCode: "", // saves the parent ASIN of the product during asin input
    parentMarketPlace: "", // saves the parent marketplace of the product during asin input
    auditElementsCount: null,
    auditElementsStatus: "idle",
    searchTermReports: {}, // Track uploaded reports by keyword index
    searchTermUploadStatus: {}, // Track upload status per keyword index
    batchUploadStatus: "idle", // Track batch upload status
    projectDetails: null, // Single project details for read-only view
    projectDetailsStatus: "idle", // Status for fetching project details
  },
  reducers: {
    setErrorInfo: (state, action) => {
      console.error("Setting error info:", action.payload);
      state.error = action.payload;
    },
    setStatusInfo: (state, action) => {
      console.error("Setting status info:", action.payload);
      state.status = action.payload;
    },
    setAsinInfo: (state, action) => {
      state.asinCode = action.payload;
    },
    setParentAsinInfo: (state, action) => {
      state.parentAsinCode = action.payload;
    },
    setParentMarketInfo: (state, action) => {
      state.parentMarketPlace = action.payload;
    },
    setProjectUrl: (state, action) => {
      state.url = action.payload;
    },
    setProjectAbout: (state, action) => {
      state.projects_data = action.payload;
    },
    setProjectMarket: (state, action) => {
      // console.log('Setting project market:', action.payload);
      state.marketPlace = action.payload;
    },
    setAmazonInfo: (state, action) => {
      state.amazonInfo = action.payload;
    },
    saveProjectId: (state, action) => {
      try {
        localStorage.setItem("current_project_id", action.payload);
        //console.log('Project ID saved to localStorage:', action.payload);
      } catch (error) {
        console.error("Failed to save project ID to localStorage:", error);
      }
    },
    saveRunId: (state, action) => {
      try {
        localStorage.setItem("current_run_id", action.payload);
        //console.log('Run ID saved to localStorage:', action.payload);
      } catch (error) {
        console.error("Failed to save run ID to localStorage:", error);
      }
    },
    setProductLink: (state, action) => {
      state.product_link = action.payload;
    },
    clearTitleAndImage: (state) => {
      state.titleAndImage = null;
    },
    setRunId: (state, action) => {
      state.runId = action.payload;
    },
    setIsProcessing: (state, action) => {
      state.isProcessing = action.payload;
    },
    setSelectedCompetitors: (state, action) => {
      state.selectedCompetitors = action.payload;
    },
    clearSelectedCompetitors: (state) => {
      state.selectedCompetitors = [];
    },
    clearExtractedUrlData: (state) => {
      state.extractedUrlData = null;
      state.extractUrlStatus = "idle";
    },
    clearAllCachedData: (state, action) => {
      // Clear session storage for specific project/run
      const { projectId, runId } = action.payload;
      const apiTypes = ["title", "features", "description", "importantInfo"];

      apiTypes.forEach((apiType) => {
        const key = getSessionStorageKey(apiType, projectId, runId);
        removeFromSessionStorage(key);
      });

      // Reset state
      state.title = null;
      state.features = null;
      state.description = null;
      state.importantInfo = null;
      state.allDataLoaded = false;
      state.asin = null; // Reset ASIN
    },
    clearAuditElementsCount: (state) => {
      state.auditElementsCount = null;
      state.auditElementsStatus = "idle";
    },
    clearSearchTermReports: (state) => {
      state.searchTermReports = {};
      state.searchTermUploadStatus = {};
    },
    setSearchTermUploadStatus: (state, action) => {
      const { keywordIndex, status } = action.payload;
      state.searchTermUploadStatus[keywordIndex] = status;
    },
  },
  extraReducers: (builder) => {
    builder
      // createProject
      .addCase(createProject.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
        state.url = action.meta.arg;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.project = action.payload.project;
        state.amazonInfo = action.payload.amazonInfo;
        state.url = action.payload.url;
      })
      .addCase(createProject.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // updateProduct
      .addCase(updateProduct.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (
          action.payload &&
          typeof action.payload === "object" &&
          action.payload.project_id
        ) {
          state.project = action.payload;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // getProduct
      .addCase(getProduct.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getProduct.fulfilled, (state, action) => {
        state.status = "succeeded";
        // console.log('Fetched product about details:', action.payload);
        state.projects_data = action.payload;
      })
      .addCase(getProduct.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // getProjectProductInfo
      .addCase(getProjectProductInfo.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getProjectProductInfo.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.productInfo = action.payload;
      })
      .addCase(getProjectProductInfo.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.productInfo = null;
      })

      // loadAllCachedData
      .addCase(loadAllCachedData.pending, (state) => {
        state.loadingAllData = true;
        state.error = null;
      })
      .addCase(loadAllCachedData.fulfilled, (state, action) => {
        state.loadingAllData = false;
        state.allDataLoaded = true;
        // The individual data will be set by their respective reducers
      })
      .addCase(loadAllCachedData.rejected, (state, action) => {
        state.loadingAllData = false;
        state.error = action.payload;
      })

      // getProjectTitle
      .addCase(getProjectTitle.pending, (state) => {
        if (!state.loadingAllData) {
          state.status = "loading";
        }
        state.error = null;
      })
      .addCase(getProjectTitle.fulfilled, (state, action) => {
        if (!state.loadingAllData) {
          state.status = "succeeded";
        }
        state.title = action.payload;
      })
      .addCase(getProjectTitle.rejected, (state, action) => {
        if (!state.loadingAllData) {
          state.status = "failed";
        }
        state.error = action.payload;
        state.title = null;
      })

      // getProjectFeatures
      .addCase(getProjectFeatures.pending, (state) => {
        if (!state.loadingAllData) {
          state.status = "loading";
        }
        state.error = null;
      })
      .addCase(getProjectFeatures.fulfilled, (state, action) => {
        if (!state.loadingAllData) {
          state.status = "succeeded";
        }
        state.features = action.payload;
      })
      .addCase(getProjectFeatures.rejected, (state, action) => {
        if (!state.loadingAllData) {
          state.status = "failed";
        }
        state.error = action.payload;
        state.features = null;
      })

      // getProjectDescription
      .addCase(getProjectDescription.pending, (state) => {
        if (!state.loadingAllData) {
          state.status = "loading";
        }
        state.error = null;
      })
      .addCase(getProjectDescription.fulfilled, (state, action) => {
        if (!state.loadingAllData) {
          state.status = "succeeded";
        }
        state.description = action.payload;
      })
      .addCase(getProjectDescription.rejected, (state, action) => {
        if (!state.loadingAllData) {
          state.status = "failed";
        }
        state.error = action.payload;
        state.description = null;
      })

      // getProjectImportantInfo
      .addCase(getProjectImportantInfo.pending, (state) => {
        if (!state.loadingAllData) {
          state.status = "loading";
        }
        state.error = null;
      })
      .addCase(getProjectImportantInfo.fulfilled, (state, action) => {
        if (!state.loadingAllData) {
          state.status = "succeeded";
        }
        state.importantInfo = action.payload;
      })
      .addCase(getProjectImportantInfo.rejected, (state, action) => {
        if (!state.loadingAllData) {
          state.status = "failed";
        }
        state.error = action.payload;
        state.importantInfo = null;
      })

      // getTitleAndImage
      .addCase(getTitleAndImage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getTitleAndImage.fulfilled, (state, action) => {
        // console.log('Title and Image fetched:', action.meta.arg.asin);
        state.status = "succeeded";
        state.titleAndImage = action.payload;
        state.asinCode = action.meta.arg.asin;
        state.error = null; // Clear any previous error
        // console.log('ASIN Code set:', state.asinCode);
      })
      .addCase(getTitleAndImage.rejected, (state, action) => {
        if (action.payload && action.payload.cancelled) {
          state.status = "cancelled";
        } else {
          state.status = "failed";
        }
        state.error = action.payload;
        state.titleAndImage = null;
      })

      // getSuggestedCompetitionUrls
      .addCase(getSuggestedCompetitionUrls.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getSuggestedCompetitionUrls.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.suggestedCompetitionUrls = action.payload;
      })
      .addCase(getSuggestedCompetitionUrls.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.suggestedCompetitionUrls = null;
      })

      // startProjectProcessing
      .addCase(startProjectProcessing.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isProcessing = true;
      })
      .addCase(startProjectProcessing.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.runId = action.payload.run_id || action.payload.runId; // Handle different response formats
        state.isProcessing = true;
      })
      .addCase(startProjectProcessing.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.isProcessing = false;
      })

      // getProcessingStatus
      .addCase(getProcessingStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(getProcessingStatus.fulfilled, (state, action) => {
        state.processingStatus = action.payload;
        // If processing is complete, set isProcessing to false
        if (
          action.payload.status === "completed" ||
          action.payload.status === "finished"
        ) {
          state.isProcessing = false;
        }
      })
      .addCase(getProcessingStatus.rejected, (state, action) => {
        state.error = action.payload;
        state.processingStatus = null;
      })

      // setProductCompetitors
      .addCase(setProductCompetitors.pending, (state) => {
        state.competitorsStatus = "loading";
        state.error = null;
      })
      .addCase(setProductCompetitors.fulfilled, (state, action) => {
        state.competitorsStatus = "succeeded";
        // You can store the response if needed
      })
      .addCase(setProductCompetitors.rejected, (state, action) => {
        state.competitorsStatus = "failed";
        state.error = action.payload;
      })

      // extractAmazonUrlData
      .addCase(extractAmazonUrlData.pending, (state) => {
        state.extractUrlStatus = "loading";
        state.error = null;
      })
      .addCase(extractAmazonUrlData.fulfilled, (state, action) => {
        // console.log('Extracted Amazon URL Data:', action.payload);
        state.extractUrlStatus = "succeeded";
        state.status = "succeeded";
        state.extractedUrlData = action.payload;
        state.amazonInfo = action.payload; // Assuming the payload contains the Amazon info
      })
      .addCase(extractAmazonUrlData.rejected, (state, action) => {
        state.extractUrlStatus = "failed";
        state.error = action.payload;
        state.extractedUrlData = null;
      })

      // updateProjectTitle
      .addCase(updateProjectTitle.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateProjectTitle.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.title = action.payload;
      })
      .addCase(updateProjectTitle.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // updateProjectFeatures
      .addCase(updateProjectFeatures.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateProjectFeatures.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.features = action.payload;
      })
      .addCase(updateProjectFeatures.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // updateProjectDescription
      .addCase(updateProjectDescription.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateProjectDescription.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.description = action.payload;
      })
      .addCase(updateProjectDescription.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // updateProjectImportantInfo
      .addCase(updateProjectImportantInfo.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateProjectImportantInfo.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.importantInfo = action.payload;
      })
      .addCase(updateProjectImportantInfo.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // getAuditElementsCount
      .addCase(getAuditElementsCount.pending, (state) => {
        state.auditElementsStatus = "loading";
        state.error = null;
      })
      .addCase(getAuditElementsCount.fulfilled, (state, action) => {
        state.auditElementsStatus = "succeeded";
        state.auditElementsCount = action.payload;
      })
      .addCase(getAuditElementsCount.rejected, (state, action) => {
        state.auditElementsStatus = "failed";
        state.error = action.payload;
        state.auditElementsCount = null;
      })

      // uploadSearchTermReports
      .addCase(uploadSearchTermReports.pending, (state, action) => {
        const { keywordIndex } = action.meta.arg;
        state.searchTermUploadStatus[keywordIndex] = "loading";
        state.error = null;
      })
      .addCase(uploadSearchTermReports.fulfilled, (state, action) => {
        const { keywordIndex, keyword, fileName, data } = action.payload;
        state.searchTermUploadStatus[keywordIndex] = "succeeded";
        state.searchTermReports[keywordIndex] = {
          keyword,
          fileName,
          uploadedAt: new Date().toISOString(),
          data
        };
      })
      .addCase(uploadSearchTermReports.rejected, (state, action) => {
        const { keywordIndex, error } = action.payload;
        state.searchTermUploadStatus[keywordIndex] = "failed";
        state.error = error;
      })

      // uploadAllSearchTermReports
      .addCase(uploadAllSearchTermReports.pending, (state) => {
        // Set all file statuses to loading
        state.batchUploadStatus = "loading";
        state.error = null;
      })
      .addCase(uploadAllSearchTermReports.fulfilled, (state, action) => {
        const { files, data } = action.payload;
        state.batchUploadStatus = "succeeded";

        // Update individual file statuses
        files.forEach(({ keywordIndex, keyword, fileName }) => {
          state.searchTermUploadStatus[keywordIndex] = "succeeded";
          state.searchTermReports[keywordIndex] = {
            keyword,
            fileName,
            uploadedAt: new Date().toISOString(),
            data
          };
        });
      })
      .addCase(uploadAllSearchTermReports.rejected, (state, action) => {
        state.batchUploadStatus = "failed";
        state.error = action.payload;
      })

      // getProjectDetails (for read-only view)
      .addCase(getProjectDetails.pending, (state) => {
        state.projectDetailsStatus = "loading";
        state.error = null;
      })
      .addCase(getProjectDetails.fulfilled, (state, action) => {
        state.projectDetailsStatus = "succeeded";
        state.projectDetails = action.payload;
      })
      .addCase(getProjectDetails.rejected, (state, action) => {
        state.projectDetailsStatus = "failed";
        state.error = action.payload;
        state.projectDetails = null;
      });
  },
});

export const {
  setProjectUrl,
  setProjectAbout,
  setProjectMarket,
  saveProjectId,
  setProductLink,
  clearTitleAndImage,
  setRunId,
  setIsProcessing,
  saveRunId,
  setSelectedCompetitors,
  clearSelectedCompetitors,
  clearExtractedUrlData,
  clearAllCachedData,
  setAmazonInfo,
  setAsinInfo,
  setParentAsinInfo,
  setParentMarketInfo,
  setErrorInfo,
  setStatusInfo,
  clearSearchTermReports,
  setSearchTermUploadStatus,
} = projectSlice.actions;
export default projectSlice.reducer;
